import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useHistory, useParams } from "react-router-dom";
import moment from "moment";
import Image from "../../components/Images/Image";
import { MAX_IMAGE_SIZE } from "../../lib/config";
import {
  showToast,
  startLoader,
  stopLoader,
  UploadImage,
} from "../../lib/global";
import {
  createNewBankPromotion,
  getBankPromotionById,
  updateBankPromotionById,
} from "../../redux/actions/bank-promotions.action";
import { breadcrumbAction } from "../../redux/actions/config.action";
import { IOSSwitch } from "../../components/switch/IOSSwitch";

function AddBankPromotion() {
  const { promoId } = useParams();
  const dispatch = useDispatch();
  const history = useHistory();

  // Form states
  const [alt, setAlt] = useState("");
  const [href, setHref] = useState("");
  const [sortOrder, setSortOrder] = useState(1);
  const [isActive, setIsActive] = useState(true);
  const [startDate, setStartDate] = useState(moment().format("YYYY-MM-DDTHH:mm"));
  const [endDate, setEndDate] = useState(moment().add(1, "month").format("YYYY-MM-DDTHH:mm"));

  // Image states
  const [imageFile, setImageFile] = useState(null);
  const [imageUrl, setImageUrl] = useState("");

  useEffect(() => {
    const breadcrumb = [
      { label: "Bank Promotions", url: "/bank-promotions" },
      { label: promoId ? "Edit Bank Promotion" : "Add Bank Promotion", url: null },
    ];
    dispatch(breadcrumbAction(breadcrumb));

    if (promoId) {
      startLoader();
      dispatch(getBankPromotionById(promoId))
        .then((res) => {
          stopLoader();
          if (res && res.data && res.data.status === 200) {
            const data = res.data.data;
            setAlt(data.alt || "");
            setHref(data.href || "");
            setSortOrder(data.sortOrder !== undefined ? data.sortOrder : 1);
            setIsActive(data.isActive !== undefined ? data.isActive : true);
            if (data.startDate) {
              setStartDate(moment(data.startDate).format("YYYY-MM-DDTHH:mm"));
            }
            if (data.endDate) {
              setEndDate(moment(data.endDate).format("YYYY-MM-DDTHH:mm"));
            }
            setImageUrl(data.image || "");
          }
        })
        .catch((err) => {
          stopLoader();
          console.error("Error fetching bank promotion data:", err);
          showToast("Failed to fetch bank promotion details", "error");
        });
    }
  }, [promoId, dispatch]);

  const handleUploadFile = (e) => {
    const fileData = e.target.files[0];
    if (!fileData) return;

    if (fileData.size > MAX_IMAGE_SIZE) {
      showToast(
        `Image with size more than ${
          MAX_IMAGE_SIZE / 1000000
        }MB is not supported.\nPlease upload image of size smaller than ${
          MAX_IMAGE_SIZE / 1000000
        }MB.\nCurrent file size: ${(fileData.size / 1000000).toFixed(2)}MB`,
        "error",
        3000,
        "bottom",
        "center"
      );
      return;
    }

    setImageFile(fileData);

    const reader = new FileReader();
    reader.readAsDataURL(fileData);
    reader.onload = function () {
      setImageUrl(reader.result);
    };
    reader.onerror = function (error) {
      console.error("FileReader Error: ", error);
    };
  };

  const validateForm = () => {
    if (!alt.trim()) {
      showToast("Alt text is required", "error");
      return false;
    }
    if (!href.trim()) {
      showToast("Target link is required", "error");
      return false;
    }
    if (sortOrder === "" || sortOrder === null) {
      showToast("Sort order is required", "error");
      return false;
    }
    if (!startDate) {
      showToast("Start date is required", "error");
      return false;
    }
    if (!endDate) {
      showToast("End date is required", "error");
      return false;
    }
    if (moment(endDate).isBefore(moment(startDate))) {
      showToast("End date cannot be before start date", "error");
      return false;
    }
    if (!imageFile && !imageUrl) {
      showToast("Promotion image is required", "error");
      return false;
    }

    return true;
  };

  const onSubmit = async () => {
    if (!validateForm()) return;

    startLoader();

    try {
      let finalImageUrl = imageUrl;

      if (imageFile) {
        const res = await UploadImage(imageFile, "bank-promotions");
        finalImageUrl = typeof res === "string" ? res : res.secure_url;
      }

      if (!finalImageUrl) {
        showToast("Image upload failed", "error");
        stopLoader();
        return;
      }

      const payload = {
        alt,
        href,
        sortOrder: parseInt(sortOrder, 10),
        isActive,
        startDate: moment(startDate).toISOString(),
        endDate: moment(endDate).toISOString(),
        image: finalImageUrl,
      };

      if (promoId) {
        dispatch(updateBankPromotionById(promoId, payload))
          .then(() => {
            stopLoader();
            showToast("Bank Promotion updated successfully", "success");
            history.push("/bank-promotions");
          })
          .catch((err) => {
            stopLoader();
            console.error("Error updating bank promotion:", err);
            showToast("Failed to update bank promotion", "error");
          });
      } else {
        dispatch(createNewBankPromotion(payload))
          .then(() => {
            stopLoader();
            showToast("Bank Promotion created successfully", "success");
            history.push("/bank-promotions");
          })
          .catch((err) => {
            stopLoader();
            console.error("Error creating bank promotion:", err);
            showToast("Failed to create bank promotion", "error");
          });
      }
    } catch (err) {
      stopLoader();
      console.error("Error submitting bank promotion form:", err);
      showToast("Error processing bank promotion data", "error");
    }
  };

  return (
    <React.Fragment>
      <div className="card m-4 p-4 shadow-sm border-0">
        <div className="d-flex justify-content-between align-items-center mb-4 pb-2 border-bottom">
          <h5 className="font-weight-bold text-uppercase mb-0">
            {promoId ? "Edit Bank Promotion" : "Create New Bank Promotion"}
          </h5>
        </div>

        <div className="row">
          {/* Left Form Column */}
          <div className="col-lg-7">
            <div className="form-group mb-3">
              <label className="font-weight-bold imp mb-1">Alt Text</label>
              <input
                type="text"
                placeholder="e.g. TS Bank Discount Promotion"
                value={alt}
                className="form-control"
                onChange={(e) => setAlt(e.target.value)}
              />
            </div>

            <div className="form-group mb-3">
              <label className="font-weight-bold imp mb-1">Target Action Link (href)</label>
              <input
                type="text"
                placeholder="e.g. /search?promo=ts"
                value={href}
                className="form-control"
                onChange={(e) => setHref(e.target.value)}
              />
            </div>

            <div className="form-group mb-3">
              <label className="font-weight-bold imp mb-1">Sort Order</label>
              <input
                type="number"
                placeholder="Display priority (e.g. 1, 2, 3)"
                value={sortOrder}
                className="form-control"
                onChange={(e) => setSortOrder(e.target.value)}
              />
            </div>

            <div className="row">
              <div className="col-md-6 form-group mb-3">
                <label className="font-weight-bold imp mb-1">Schedule Start Date & Time</label>
                <input
                  type="datetime-local"
                  className="form-control"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                />
              </div>
              <div className="col-md-6 form-group mb-3">
                <label className="font-weight-bold imp mb-1">Schedule End Date & Time</label>
                <input
                  type="datetime-local"
                  className="form-control"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                />
              </div>
            </div>

            <div className="form-group mb-3 d-flex align-items-center mt-2">
              <label className="font-weight-bold mr-3 mb-0">Active Status</label>
              <IOSSwitch checked={isActive} onChange={(e) => setIsActive(e.target.checked)} />
            </div>
          </div>

          {/* Right Image Upload Column */}
          <div className="col-lg-5 border-left pl-lg-4">
            <h6 className="font-weight-bold mb-3 text-secondary">PROMO IMAGE</h6>
            <div className="mb-4">
              <label className="font-weight-bold imp mb-1 d-block">
                Bank Logo / Offer Card <span className="text-muted">(Recommended: 380 x 236 px)</span>
              </label>
              <input
                accept="image/*"
                id="bank-promo-image-upload"
                className="d-none"
                onChange={handleUploadFile}
                type="file"
              />
              <div className="border rounded p-3 text-center bg-light">
                <label htmlFor="bank-promo-image-upload" className="pointer m-0 d-block">
                  <Image
                    src={imageUrl || "/icons/upload_icon.svg"}
                    alt="promo preview"
                    style={{
                      width: "100%",
                      maxHeight: "220px",
                      objectFit: "contain",
                      backgroundColor: "#f5f5f5",
                    }}
                  />
                  <div className="mt-2 text-primary font-weight-bold small">
                    {imageFile || imageUrl ? "Change Image" : "Upload Image"}
                  </div>
                </label>
              </div>
            </div>
            <div className="text-muted small bg-light p-2 rounded border mt-2">
              <strong>Dimensions requirements:</strong> For best visual styling and alignment on storefront checkout grids, upload an image of exactly 380x236px. Maximum file size: 3MB.
            </div>
          </div>
        </div>
      </div>

      {/* Footer controls */}
      <footer className="d-flex position-sticky b-0 flex-wrap justify-content-end align-items-center py-3 mt-3 bg-white border-top">
        <div className="col-md-12">
          <div className="d-flex justify-content-end pr-3">
            <button
              type="button"
              className="btn btn-outline-danger px-4 mr-2"
              onClick={history.goBack}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn btn-dark px-4"
              onClick={onSubmit}
            >
              {promoId ? "Save Changes" : "Create Bank Promotion"}
            </button>
          </div>
        </div>
      </footer>
    </React.Fragment>
  );
}

export default AddBankPromotion;
